import React, { useState } from "react";
import { toast } from "sonner";
import { Search, Scissors } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { LoanTable, ChangeList } from "./LoanTable";
import { ConfirmDialog } from "./ConfirmDialog";

const BASE = "/operations/payoffs/client-groups";

export const ManualToolsPanel = ({ onChanged }) => {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [splitLoan, setSplitLoan] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    try {
      const { data } = await api.get(`${BASE}/search`, { params: { q: q.trim() } });
      setGroups(data); setSelected([]); setTarget(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = (gid) => setSelected((s) => s.includes(gid) ? s.filter((x) => x !== gid) : [...s, gid]);
  const sel = (groups || []).filter((g) => g.group_id && selected.includes(g.group_id));
  const tgt = target && selected.includes(target) ? target : selected[0];
  const changes = sel.filter((g) => g.group_id !== tgt).flatMap((g) => g.loans.map((l) => ({
    loan_id: l.loan_id, client_name: l.client_name, from_group_id: g.group_id, to_group_id: tgt })));

  const merge = async (note) => {
    try {
      for (const g of sel.filter((x) => x.group_id !== tgt)) {
        await api.post(`${BASE}/merge`, { source_group_id: g.group_id, target_group_id: tgt, note });
      }
      toast.success(`Merged into ${tgt}`); await search(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  const split = async (note) => {
    try {
      const { data } = await api.post(`${BASE}/split`, { loan_id: splitLoan.loan_id, note });
      toast.success(`${splitLoan.loan_id} moved to new group ${data.new_group_id}`); await search(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  return (
    <div className="space-y-4" data-testid="manual-tools-panel">
      <div className="p-4 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#374151]">
        <div className="font-semibold text-[#0A2540]">Manual merge / split</div>
        <p className="mt-1">Use this to fix duplicates or wrong groupings caused by data-entry mistakes. Search by client name, loan ID, client email, attorney email or group ID. Loans with no group yet get one from the safe assignments step first.</p>
      </div>
      <form onSubmit={search} className="flex gap-2 max-w-xl">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Prunner, L-3A3B…, CG-…" data-testid="group-search-input" />
        <Button type="submit" data-testid="group-search-btn"><Search className="w-4 h-4 mr-1" />Search</Button>
      </form>
      {sel.length >= 2 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 p-3 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] text-sm" data-testid="merge-bar">
          <span>Merge {sel.length} groups into</span>
          <select value={tgt} onChange={(e) => setTarget(e.target.value)} className="h-8 rounded border border-[#D1D5DB] px-2 font-mono text-xs" data-testid="merge-target-select">
            {sel.map((g) => <option key={g.group_id} value={g.group_id}>{g.group_id}</option>)}
          </select>
          <Button size="sm" onClick={() => setMergeOpen(true)} data-testid="merge-open-btn" className="bg-[#0066FF] hover:bg-[#0052CC] text-white">Review merge…</Button>
        </div>
      )}
      {groups && groups.length === 0 && <div className="text-sm text-[#6B7280]" data-testid="group-search-empty">No matching loans.</div>}
      {(groups || []).map((g, i) => (
        <div key={g.group_id || `u-${i}`} className="border border-[#E5E7EB] rounded-xl p-4 bg-white" data-testid={`group-result-${g.group_id || g.loans[0].loan_id}`}>
          <div className="flex items-center gap-3 mb-2 text-xs">
            {g.group_id ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={selected.includes(g.group_id)} onCheckedChange={() => toggle(g.group_id)} data-testid={`group-select-${g.group_id}`} />
                <span className="font-mono font-semibold text-[#0A2540]">{g.group_id}</span>
              </label>
            ) : <span className="font-semibold text-[#92400E]">Not grouped yet</span>}
            <span className="text-[#6B7280]">{g.loans.length} loan(s) · {g.active_count} active</span>
          </div>
          <LoanTable rows={g.loans} testid={`group-table-${g.group_id || g.loans[0].loan_id}`}
            extraCol={g.group_id && g.loans.length > 1 ? (r) => (
              <Button size="sm" variant="outline" onClick={() => setSplitLoan(r)} data-testid={`split-btn-${r.loan_id}`}>
                <Scissors className="w-3.5 h-3.5 mr-1" />Split out
              </Button>) : null} />
        </div>
      ))}

      <ConfirmDialog open={mergeOpen} onOpenChange={setMergeOpen} testid="manual-merge-dialog" confirmLabel="Merge groups"
        title={`Merge into ${tgt}?`} requireNote notePlaceholder="Reason (required)"
        description="Only client_group_id changes. Loan records, balances and firm_id stay as they are."
        onConfirm={merge}>
        <ChangeList changes={changes} testid="manual-merge-change-list" />
      </ConfirmDialog>
      <ConfirmDialog open={!!splitLoan} onOpenChange={(o) => !o && setSplitLoan(null)} testid="split-dialog" confirmLabel="Split into own group"
        title={`Split ${splitLoan?.loan_id} out of ${splitLoan?.client_group_id}?`} requireNote notePlaceholder="Reason (required)"
        description="This loan gets a new client_group_id of its own and won't be included in this client's consolidated payoffs anymore."
        onConfirm={split} danger>
        {splitLoan && <ChangeList changes={[{ loan_id: splitLoan.loan_id, client_name: splitLoan.client_name, from_group_id: splitLoan.client_group_id, to_group_id: null }]} testid="split-change-list" />}
      </ConfirmDialog>
    </div>
  );
};
